import { createContext, useContext, useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import type { BoardTicket, TicketBoard, TicketFields } from '@one-deployment/api'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { enc, post } from '@/lib/api'
import { cn } from '@/lib/cn'

/**
 * Changing a ticket where it is shown (a card's chips, the side panel, the
 * attention list): estimate, owner, priority, cycle. One Linear write each
 * through the outbox; the board shows the change at once. With Linear writes
 * off for the project the chips still open, read-only, and say why.
 */
export type Person = { id: string; name: string; avatar: string | null }
export type Edit = {
  can: boolean
  /** Why editing is off, shown in the picker. */
  why: string | null
  people: Person[]
  cycles: { id: string; label: string }[]
  set: (t: BoardTicket, f: TicketFields) => void
  error: Error | null
  reset: () => void
}

const Ctx = createContext<Edit | null>(null)
export const EditProvider = ({ value, children }: { value: Edit; children: ReactNode }) => <Ctx.Provider value={value}>{children}</Ctx.Provider>
export const useEdit = () => useContext(Ctx)

/** The board's people with their Linear pictures (an older worker sent none: then from tickets they own). */
export function peopleOf(board: TicketBoard): Person[] {
  const pic = new Map(board.tickets.filter((t) => t.ownerId && t.ownerAvatar).map((t) => [t.ownerId!, t.ownerAvatar!]))
  return board.people.map((p) => ({ id: p.id, name: p.name, avatar: p.avatar ?? pic.get(p.id) ?? null }))
}

export function useFields(project: string) {
  const qc = useQueryClient()
  const key = ['project', project, 'tickets']
  return useMutation({
    mutationFn: (v: { t: BoardTicket; f: TicketFields }) => post(`/api/projects/${enc(project)}/tickets/${enc(v.t.id)}/fields`, v.f),
    onMutate: async ({ t, f }) => {
      await qc.cancelQueries({ queryKey: key })
      const before = qc.getQueryData<TicketBoard>(key)
      if (before) qc.setQueryData<TicketBoard>(key, { ...before, tickets: before.tickets.map((x) => x.id === t.id ? applyFields(x, f, before) : x) })
      return { before }
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(key, ctx.before) },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  })
}

/** The ticket as it will be once the write lands, so the card does not wait for Linear. */
export function applyFields(t: BoardTicket, f: TicketFields, board: Pick<TicketBoard, 'people' | 'tickets'>): BoardTicket {
  const out = { ...t, updatedAt: Date.now() }
  if (f.points !== undefined) out.estimate = f.points
  if (f.priority !== undefined) out.priority = f.priority || null
  if (f.cycleId !== undefined) out.cycleId = f.cycleId
  if (f.ownerId !== undefined) {
    out.ownerId = f.ownerId
    out.owner = f.ownerId ? board.people.find((p) => p.id === f.ownerId)?.name ?? null : null
    out.ownerAvatar = f.ownerId ? board.tickets.find((x) => x.ownerId === f.ownerId && x.ownerAvatar)?.ownerAvatar ?? null : null
  }
  return out
}

/** A small menu of choices behind a chip; the chip itself never opens the ticket. */
export function Picker<V extends string | number | null>({ trigger, label, options, value, onPick, disabledWhy, align = 'start' }: {
  trigger: ReactNode; label: string; value: V; onPick: (v: V) => void
  options: { value: V; label: ReactNode; hint?: string }[]
  disabledWhy: string | null; align?: 'start' | 'end'
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={label} title={label}
          onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
          className="rounded-sm outline-none hover:ring-1 hover:ring-border-strong focus-visible:ring-1 focus-visible:ring-accent">
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-56 p-1" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <p className="px-2 pb-1 pt-0.5 text-xs font-medium text-fg-subtle">{label}</p>
        <ul className="max-h-72 overflow-y-auto">
          {options.map((o) => (
            <li key={String(o.value)}>
              <button type="button" disabled={!!disabledWhy} onClick={() => { setOpen(false); if (o.value !== value) onPick(o.value) }}
                className={cn('flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-hover disabled:cursor-not-allowed disabled:opacity-60', o.value === value ? 'text-fg' : 'text-fg-muted')}>
                <Check size={12} className={o.value === value ? 'opacity-100' : 'opacity-0'} />
                <span className="flex min-w-0 flex-1 items-center gap-2 truncate">{o.label}</span>
                {o.hint && <span className="shrink-0 text-[10px] text-fg-faint">{o.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
        {disabledWhy && <p className="border-t border-border px-2 pt-1.5 text-[10px] leading-snug text-fg-faint">{disabledWhy}</p>}
      </PopoverContent>
    </Popover>
  )
}
